import { MERCHANT } from "constants/action-types";
import { Observable } from "rxjs";
import { ActionsObservable } from "redux-observable";
import uuid from "uuid/v4";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import {
    ListCollectionVersionStatutesRequestAction,
    listCollectionVersionStatusesComplete,
    CreateCollectionVersionStatusRequestAction,
    createCollectionVersionStatusComplete,
    UpdateCollectionVersionStatusRequestAction,
    updateCollectionVersionStatusComplete,
    SortCollectionVersionStatusRequestAction,
    sortCollectionVersionStatusComplete,
    RemoveCollectionVersionStatusRequestAction,
    removeCollectionVersionStatusComplete,
} from "actions/merchant";
import { formatMessage } from "lib/globalize-util";
import {
    buildCollectionVersionStatusMutateOperation,
    CollectionVersionStatusOperationKey,
} from "helpers/collection-version-status";
import { add as snackbarAdd } from "actions/snackbars";

export const listCollectionVersionStatutes = (action$: ActionsObservable<ListCollectionVersionStatutesRequestAction>) =>
    action$.ofType(MERCHANT.COLLECTION_VERSION_STATUS.LIST.REQUEST).switchMap(({ meta }) => {
        const fetchErrorMessage = formatMessage("Error fetching collection version status");
        const { merchantId, collectionId } = meta;
        let path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/collection_version_statuses`;
        if (collectionId) {
            path = `${path}?collection_id=${collectionId}`;
        }

        return ajax({
            method: "GET",
            url: path,
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || fetchErrorMessage);
                }
                return [listCollectionVersionStatusesComplete(response.data, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(listCollectionVersionStatusesComplete([], merchantId)),
                    Observable.of(snackbarAdd({ message: error.message }))
                )
            );
    });

export const createCollectionVersionStatus = (action$: ActionsObservable<CreateCollectionVersionStatusRequestAction>) =>
    action$.ofType(MERCHANT.COLLECTION_VERSION_STATUS.CREATE.REQUEST).switchMap(({ payload, meta }) => {
        const createErrorMessage = formatMessage("Unknown error creating collection version status");
        const { index, collectionVersionStatus } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/collection_version_statuses`;

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations: [
                    buildCollectionVersionStatusMutateOperation(CollectionVersionStatusOperationKey.Insert, {
                        index,
                        value: {
                            ...collectionVersionStatus,
                            // TODO: This should be auto-generated by BE
                            // Auto-generate id.
                            id: uuid(),
                        },
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage(createErrorMessage));
                }

                // Response is list of all results with the new row.
                return [
                    createCollectionVersionStatusComplete(response.data, merchantId),
                    snackbarAdd({
                        message: formatMessage("Merchant collection version status: |name| created", {
                            name: collectionVersionStatus.label,
                        }),
                    }),
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(createCollectionVersionStatusComplete(error, merchantId)),
                    Observable.of(snackbarAdd({ message: error.message }))
                )
            );
    });

export const updateCollectionVersionStatus = (
    action$: ActionsObservable<UpdateCollectionVersionStatusRequestAction>
) => {
    let buffers = {};
    return action$
        .ofType(MERCHANT.COLLECTION_VERSION_STATUS.UPDATE.REQUEST)
        .map((action) => {
            const { meta } = action;

            // buffers example: { "123Merchant": [action1, action2], "124Merchant": [action3, action4] }
            const bufferKey = meta.merchantId;
            if (!buffers[bufferKey]) {
                buffers[bufferKey] = [action];
            } else {
                buffers[bufferKey] = buffers[bufferKey].concat([action]);
            }

            return action;
        })
        .debounce(() => Observable.interval(50))
        .map(() => {
            const groupedBufferedActions = Object.keys(buffers).map((key) => buffers[key]);
            buffers = {};

            // This is array of array. Eg: [[action1, action2], [action3, action4]]
            return groupedBufferedActions;
        })
        .flatMap((groupedBufferedActions) => {
            return Observable.from(groupedBufferedActions).flatMap((bufferedActions) => {
                const updateErrorMessage = formatMessage("Unknown error updating collection version status");
                // bufferedActions contains actions with same merchant id
                // See how they were grouped before debounce. Thus, we can take first element to represent the whole actions
                const { meta } = bufferedActions[0];

                const { merchantId } = meta;
                const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/collection_version_statuses`;

                const operations = bufferedActions.reduce((acc, action) => {
                    const { payload } = action;
                    acc.push(
                        buildCollectionVersionStatusMutateOperation(CollectionVersionStatusOperationKey.Set, {
                            index: payload.index,
                            value: payload.collectionVersionStatus,
                        })
                    );

                    return acc;
                }, []);

                return ajax({
                    method: "PUT",
                    url: path,
                    body: {
                        operations,
                    },
                })
                    .flatMap(({ response, status }) => {
                        if (status !== 200) {
                            throw new Error(response.message || updateErrorMessage);
                        }

                        // Response is list of all results
                        return [updateCollectionVersionStatusComplete(response.data, merchantId)];
                    })
                    .catch((error) =>
                        Observable.merge(
                            Observable.of(updateCollectionVersionStatusComplete(error, merchantId)),
                            Observable.of(snackbarAdd({ message: error.message }))
                        )
                    );
            });
        });
};

export const sortCollectionVersionStatus = (action$: ActionsObservable<SortCollectionVersionStatusRequestAction>) =>
    action$.ofType(MERCHANT.COLLECTION_VERSION_STATUS.SORT.REQUEST).switchMap(({ payload, meta }) => {
        const sortErrorMessage = formatMessage("Unknown error sorting collection version status");
        const { index, destinationIndex } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/collection_version_statuses`;

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations: [
                    buildCollectionVersionStatusMutateOperation(CollectionVersionStatusOperationKey.Move, {
                        index,
                        destination_index: destinationIndex,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || formatMessage(sortErrorMessage));
                }

                return [sortCollectionVersionStatusComplete(undefined, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(sortCollectionVersionStatusComplete(error, merchantId)),
                    Observable.of(snackbarAdd({ message: error.message }))
                )
            );
    });

export const removeCollectionVersionStatus = (action$: ActionsObservable<RemoveCollectionVersionStatusRequestAction>) =>
    action$.ofType(MERCHANT.COLLECTION_VERSION_STATUS.REMOVE.REQUEST).switchMap(({ payload, meta }) => {
        const removeErrorMessage = formatMessage("Unknown error removing collection version status");
        const { index } = payload;
        const { merchantId } = meta;
        const path = `${siteConfig.goAPI}/v1/collections/merchants/${merchantId}/collection_version_statuses`;

        return ajax({
            method: "PUT",
            url: path,
            body: {
                operations: [
                    buildCollectionVersionStatusMutateOperation(CollectionVersionStatusOperationKey.Remove, {
                        index,
                    }),
                ],
            },
        })
            .flatMap(({ response, status }) => {
                if (status !== 200) {
                    throw new Error(response.message || removeErrorMessage);
                }

                return [removeCollectionVersionStatusComplete(undefined, merchantId)];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of(removeCollectionVersionStatusComplete(error, merchantId)),
                    Observable.of(snackbarAdd({ message: error.message }))
                )
            );
    });
